import React, { useState, useRef, useEffect } from 'react'
import { Button } from '@/atoms/Button'
import { PlayOutlineIcon } from '@/atoms/Icons/PlayOutlineIcon'
import { Image } from '@/atoms/Image'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { CaptionSM, ParagraphLG } from '@/atoms/Text'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { GDisplay, GTitleXL } from '@/views/PreSales/PreSalesGiveawayView/components/UI/Typography'
import { useYoungWashingtonTickets } from '../hooks/useYoungWashingtonTickets'

export default function Hero() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const { handleBuyTicketsClick, ctaText } = useYoungWashingtonTickets()

  useEffect(() => {
    if (isModalOpen && videoRef.current) {
      videoRef.current.pause()
    } else if (!isModalOpen && videoRef.current) {
      videoRef.current.play()
    }
  }, [isModalOpen])

  const scrollToNextSection = () => {
    const nextSection = document.getElementById('behind-the-camera-section')
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <section className="relative flex min-h-screen flex-col md:h-[90vh] md:min-h-0">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <Image
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          src="v1759419819/theatrical/young%20washington/yw-trailer-img-screen-01.webp"
          fill
        />

        <video
          ref={videoRef}
          className="absolute inset-0 h-full min-w-full object-cover"
          autoPlay
          playsInline
          muted
          loop
          src="https://images.angelstudios.com/video/upload/v1759419785/theatrical/young%20washington/Young_Washington_Trailer_Loop_01.mp4"
        />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10 mix-blend-multiply"
          style={{
            backgroundImage:
              "url('https://images.angelstudios.com/image/upload/c_limit,w_1200,q_50/v1759419818/theatrical/young%20washington/dirt-texture-on-white-01.webp')",
          }}
        >
          <Image
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            src="v1759419818/theatrical/young%20washington/dirt-texture-on-white-01.webp"
            sizes="(max-width: 1200px)"
            quality={50}
            fill
          />
        </div>
        <div
          className="absolute inset-0 z-10"
          style={{
            background: 'linear-gradient(rgba(0, 41, 92, 0.5) 0%, rgb(0, 21, 46) 100%)',
          }}
        />
      </div>

      <div className="relative z-20 flex flex-1 flex-col items-center justify-center px-8 text-center text-white se:justify-start se:pt-20">
        <div className="mb-6 md:mb-8">
          <Image
            className="shrink-0 rounded-lg md:w-[355px] "
            alt="Young Washington Logo"
            width={210}
            height={130}
            src={'v1759419818/theatrical/young%20washington/Young-Washington-Logo-Red-Wonder.webp'}
          />
        </div>
        <AngelLogo height={44} className="absolute left-4 top-4 md:left-8 md:top-8" />

        <GDisplay className="hidden text-balance uppercase md:block">
          The Untold Origin of America&apos;s First Hero
        </GDisplay>
        <GTitleXL className="mb-2 text-balance uppercase md:hidden">
          The Untold Origin of America&apos;s First Hero
        </GTitleXL>

        <ParagraphLG className="mb-10 text-pretty !font-roboto se:mb-6 se:!text-base">
          Pre-Order tickets to Young Washington, in theaters Independence Day, 2026.
        </ParagraphLG>

        <div className="flex flex-wrap justify-center gap-4">
          <Button
            variant={'oxide-primary'}
            outline={false}
            size={'lg'}
            className={'w-full !border-[#DA2031] !bg-[#DA2031] !font-roboto md:w-fit'}
            onClick={() => handleBuyTicketsClick('make-history')}
          >
            {ctaText}
          </Button>

          <Button
            size={'lg'}
            variant={'core-gray-800'}
            onClick={() => setIsModalOpen(true)}
            className={'w-full !font-roboto md:w-fit'}
          >
            <PlayOutlineIcon color={'white'} size={24} /> Watch Trailer
          </Button>
        </div>

        <YoutubeVideoModal
          showModal={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          youTubeUrl="https://www.youtube-nocookie.com/embed/_Q3WkycWjLc"
        />

        <button
          onClick={scrollToNextSection}
          className="absolute bottom-4 left-1/2 z-30 -translate-x-1/2 cursor-pointer md:bottom-16"
          aria-label="Scroll to next section"
        >
          <CaptionSM className={'mb-5 !font-roboto text-white'}>Unlock Exclusive Offer</CaptionSM>
          <div className="mx-auto w-[17px] animate-bounce">
            <Image
              alt="Scroll down"
              width={17}
              height={32}
              src={'v1759419810/theatrical/young%20washington/icon-down-arrow_3x.webp'}
            />
          </div>
        </button>
      </div>
    </section>
  )
}
